import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { addEdge } from '@xyflow/react';
import { toast } from '../../../utils/toast';
import { useWorkflow } from '../../../components/Admin/Workflows/Wizard/WorkflowContext';
import {
  NODE_CATEGORIES,
  WORKFLOW_DEFAULTS,
} from '../../../constants/workflow.constant';
import {
  findUpstreamNodeIds,
  getAssessmentParentReviewId,
  findDownstreamFormIds,
} from '../../../components/Admin/Workflows/Wizard/utils/graphHelpers';

const useConnectionValidation = () => {
  const { t } = useTranslation();
  const { nodes, edges, setEdges } = useWorkflow();

  const validateReviewNodeForms = useCallback(
    (reviewNode, currentEdges, newEdge) => {
      const formIds = reviewNode.data?.formConfig?.form_ids || [];
      const singleFormId = reviewNode.data?.formConfig?.form_id;
      const reviewFormIds =
        formIds.length > 0 ? formIds : singleFormId ? [singleFormId] : [];

      if (reviewFormIds.length === 0) return { valid: true };

      const upstreamIds = findUpstreamNodeIds(
        reviewNode.id,
        currentEdges,
        newEdge
      );

      const upstreamSubmissionFormIds = new Set();
      for (const nodeId of upstreamIds) {
        const node = nodes.find((n) => n.id === nodeId);
        if (node?.category === NODE_CATEGORIES.SUBMISSION) {
          const nodeFormIds = node.data?.formConfig?.form_ids || [];
          const nodeSingleFormId = node.data?.formConfig?.form_id;
          if (nodeFormIds.length > 0) {
            nodeFormIds.forEach((id) => upstreamSubmissionFormIds.add(id));
          } else if (nodeSingleFormId) {
            upstreamSubmissionFormIds.add(nodeSingleFormId);
          }
        }
      }

      const reviewStageName =
        reviewNode.data?.formConfig?.name || t('workflowBuilder.reviewStage');

      for (const formId of reviewFormIds) {
        if (!upstreamSubmissionFormIds.has(formId)) {
          return {
            valid: false,
            message: t('workflowValidation.reviewOrderError', {
              reviewName: reviewStageName,
            }),
          };
        }
      }

      return { valid: true };
    },
    [nodes, t]
  );

  const onConnect = useCallback(
    (params) => {
      const currentEdges = edges;
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);

      const isTargetActionNode =
        targetNode?.category === NODE_CATEGORIES.CHILD_APP ||
        targetNode?.category === NODE_CATEGORIES.CHILD_REJ ||
        targetNode?.category === NODE_CATEGORIES.CHILD_SBK ||
        targetNode?.category === NODE_CATEGORIES.CHILD_STA;

      if (
        isTargetActionNode &&
        sourceNode?.category !== NODE_CATEGORIES.REVIEW &&
        sourceNode?.category !== NODE_CATEGORIES.ASSESSMENT_REVIEW
      ) {
        toast.error(
          t('workflowValidation.actionNodeMustConnectFromReview', {
            name: targetNode?.data?.name || t('workflowBuilder.action'),
          })
        );
        return;
      }

      if (
        sourceNode?.category === NODE_CATEGORIES.REVIEW &&
        targetNode?.category === NODE_CATEGORIES.END
      ) {
        toast.error(t('workflowValidation.reviewCannotConnectToEnd'));
        return;
      }

      if (targetNode?.category === NODE_CATEGORIES.SUBMISSION) {
        const incomingEdges = currentEdges.filter(
          (edge) => edge.target === params.target
        );
        const incomingFromSubmission = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.SUBMISSION;
        });
        const incomingFromReview = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.REVIEW;
        });
        const incomingFromStart = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.START;
        });
        const incomingFromApprove = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.CHILD_APP;
        });
        const incomingFromReject = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.CHILD_REJ;
        });

        const normalIncomingCount =
          incomingFromSubmission.length +
          incomingFromReview.length +
          incomingFromStart.length;
        const isNewConnectionFromSendBack =
          sourceNode?.category === NODE_CATEGORIES.CHILD_SBK;
        const isNewConnectionFromApproveOrReject =
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ;

        if (
          !isNewConnectionFromSendBack &&
          !isNewConnectionFromApproveOrReject &&
          normalIncomingCount >= 1
        ) {
          toast.error(
            t('workflowValidation.submissionNodeTooManyNormalIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }

        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP &&
          incomingFromApprove.length >= 1
        ) {
          toast.error(
            t('workflowValidation.submissionNodeMaxOneApproveIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }

        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ &&
          incomingFromReject.length >= 1
        ) {
          toast.error(
            t('workflowValidation.submissionNodeMaxOneRejectIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }
      }

      if (sourceNode?.category === NODE_CATEGORIES.SUBMISSION) {
        const outgoingCount = currentEdges.filter(
          (edge) => edge.source === params.source
        ).length;
        if (outgoingCount >= 1) {
          toast.error(
            t('workflowValidation.submissionNodeSingleOutgoing', {
              name: sourceNode?.data?.formConfig?.name || sourceNode?.id,
            })
          );
          return;
        }
      }

      if (targetNode?.category === NODE_CATEGORIES.REVIEW) {
        let isEdgeFromAssessmentReview = false;
        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ
        ) {
          const sourceIncoming = currentEdges.filter(
            (edge) => edge.target === sourceNode?.id
          );
          for (const edge of sourceIncoming) {
            const assessmentReviewNode = nodes.find(
              (n) => n.id === edge.source
            );
            if (
              assessmentReviewNode?.category ===
              NODE_CATEGORIES.ASSESSMENT_REVIEW
            ) {
              isEdgeFromAssessmentReview = true;
              break;
            }
          }
        }

        const incomingEdges = currentEdges.filter(
          (edge) => edge.target === params.target
        );
        const incomingFromSubmission = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.SUBMISSION;
        });
        const incomingFromReview = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.REVIEW;
        });
        const incomingFromStart = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.START;
        });
        const incomingFromApprove = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.CHILD_APP;
        });
        const incomingFromReject = incomingEdges.filter((edge) => {
          const srcNode = nodes.find((n) => n.id === edge.source);
          return srcNode?.category === NODE_CATEGORIES.CHILD_REJ;
        });

        const normalIncomingCount =
          incomingFromSubmission.length +
          incomingFromReview.length +
          incomingFromStart.length;
        const isNewConnectionFromSendBack =
          sourceNode?.category === NODE_CATEGORIES.CHILD_SBK;
        const isNewConnectionFromApproveOrReject =
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ;

        if (
          !isNewConnectionFromSendBack &&
          !isNewConnectionFromApproveOrReject &&
          normalIncomingCount >= 1 &&
          !isEdgeFromAssessmentReview
        ) {
          toast.error(
            t('workflowValidation.reviewNodeTooManyNormalIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }

        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP &&
          incomingFromApprove.length >= 1 &&
          !isEdgeFromAssessmentReview
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMaxOneApproveIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }

        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ &&
          incomingFromReject.length >= 1 &&
          !isEdgeFromAssessmentReview
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMaxOneRejectIncoming', {
              name: targetNode?.data?.formConfig?.name || targetNode?.id,
            })
          );
          return;
        }
      }

      if (
        targetNode?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW &&
        sourceNode?.category !== NODE_CATEGORIES.CHILD_STA
      ) {
        toast.error(
          t(
            'workflowValidation.assessmentReviewMustConnectFromSendToAssessmentOnly'
          )
        );
        return;
      }

      if (targetNode?.category === NODE_CATEGORIES.CHILD_STA) {
        const childNodeIds = currentEdges
          .filter((edge) => edge.source === targetNode.id)
          .map((edge) => edge.target);
        const grandChildNodeIds = currentEdges
          .filter((edge) => childNodeIds.includes(edge.source))
          .map((edge) => edge.target);
        const grandChildrenTargets = currentEdges
          .filter((edge) => grandChildNodeIds.includes(edge.source))
          .map((edge) => edge.target);

        if (
          grandChildrenTargets.length > 0 &&
          !grandChildrenTargets.includes(params.source)
        ) {
          toast.error(
            t(
              'workflowValidation.sendToAssessmentMustConnectFromSameReviewStage'
            )
          );
          return;
        }
      }

      if (targetNode?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW) {
        const childNodeIds = currentEdges
          .filter((edge) => edge.source === targetNode.id)
          .map((edge) => edge.target);
        const childrenTargets = currentEdges
          .filter((edge) => childNodeIds.includes(edge.source))
          .map((edge) => edge.target);
        const parentNode = currentEdges.find(
          (edge) => edge.target === params.source
        )?.source;

        if (
          !parentNode ||
          (childrenTargets.length > 0 && !childrenTargets.includes(parentNode))
        ) {
          toast.error(
            t(
              'workflowValidation.sendToAssessmentMustConnectFromSameReviewStage'
            )
          );
          return;
        }
      }

      if (
        sourceNode?.category === NODE_CATEGORIES.REVIEW ||
        sourceNode?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW
      ) {
        const outgoingEdges = currentEdges.filter(
          (edge) =>
            edge.source === params.source &&
            !(edge.source === params.source && edge.target === params.target)
        );

        const approveCount = outgoingEdges.filter((edge) => {
          const tgtNode = nodes.find((n) => n.id === edge.target);
          return tgtNode?.category === NODE_CATEGORIES.CHILD_APP;
        }).length;
        const rejectCount = outgoingEdges.filter((edge) => {
          const tgtNode = nodes.find((n) => n.id === edge.target);
          return tgtNode?.category === NODE_CATEGORIES.CHILD_REJ;
        }).length;
        const sendBackCount = outgoingEdges.filter((edge) => {
          const tgtNode = nodes.find((n) => n.id === edge.target);
          return tgtNode?.category === NODE_CATEGORIES.CHILD_SBK;
        }).length;
        const sendToAssessmentCount = outgoingEdges.filter((edge) => {
          const tgtNode = nodes.find((n) => n.id === edge.target);
          return tgtNode?.category === NODE_CATEGORIES.CHILD_STA;
        }).length;

        if (
          targetNode?.category === NODE_CATEGORIES.CHILD_APP &&
          approveCount >= 1
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMultipleApprove', {
              name: sourceNode?.data?.formConfig?.name || sourceNode?.id,
            })
          );
          return;
        }

        if (
          targetNode?.category === NODE_CATEGORIES.CHILD_REJ &&
          rejectCount >= 1
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMultipleReject', {
              name: sourceNode?.data?.formConfig?.name || sourceNode?.id,
            })
          );
          return;
        }

        if (
          targetNode?.category === NODE_CATEGORIES.CHILD_SBK &&
          sendBackCount >= 1
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMultipleSendBack', {
              name: sourceNode?.data?.formConfig?.name || sourceNode?.id,
            })
          );
          return;
        }

        if (
          targetNode?.category === NODE_CATEGORIES.CHILD_STA &&
          sendToAssessmentCount >= 1
        ) {
          toast.error(
            t('workflowValidation.reviewNodeMultipleSendToAssessment', {
              name: sourceNode?.data?.formConfig?.name || sourceNode?.id,
            })
          );
          return;
        }
      }

      const styledEdge = {
        ...params,
        type: WORKFLOW_DEFAULTS.EDGE_TYPE,
        style: WORKFLOW_DEFAULTS.EDGE_STYLE,
      };

      const addNewEdge = () => {
        setEdges((eds) => {
          const edgeExists = eds.some(
            (edge) =>
              edge.source === params.source && edge.target === params.target
          );
          if (edgeExists) return eds;
          return addEdge(styledEdge, eds);
        });
      };

      if (sourceNode?.category === NODE_CATEGORIES.ASSESSMENT_REVIEW) {
        if (
          targetNode?.category !== NODE_CATEGORIES.CHILD_APP &&
          targetNode?.category !== NODE_CATEGORIES.CHILD_REJ
        ) {
          toast.error(
            t('workflowValidation.assessmentReviewMustConnectToApproveOrReject')
          );
          return;
        }
        const targetIncoming = currentEdges.filter(
          (edge) => edge.target === targetNode?.id
        );
        if (targetIncoming.length > 0) {
          toast.error(
            t(
              'workflowValidation.assessmentReviewMustConnectToNewApproveOrReject'
            )
          );
          return;
        }

        const targetOutgoingEdges = currentEdges.filter(
          (edge) => edge.source === targetNode?.id
        );
        if (targetOutgoingEdges.length > 0) {
          const parentReviewId = getAssessmentParentReviewId(
            sourceNode.id,
            currentEdges,
            nodes
          );
          const hasInvalidTarget = targetOutgoingEdges.some(
            (edge) => edge.target !== parentReviewId
          );
          if (!parentReviewId || hasInvalidTarget) {
            toast.error(
              t('workflowValidation.approveRejectMustConnectToParentReview')
            );
            return;
          }
        }

        addNewEdge();
        return;
      }

      if (targetNode?.category === NODE_CATEGORIES.CHILD_SBK) {
        const hasIncomingEdge = currentEdges.some(
          (edge) => edge.target === params.target
        );
        if (hasIncomingEdge) {
          toast.error(
            t('workflowValidation.sendBackSingleIncoming', {
              name: targetNode?.data?.name || t('workflowBuilder.sendBack'),
            })
          );
          return;
        }
        addNewEdge();
        return;
      }

      if (targetNode?.category === NODE_CATEGORIES.CHILD_STA) {
        const hasIncomingEdge = currentEdges.some(
          (edge) => edge.target === params.target
        );
        if (hasIncomingEdge) {
          toast.error(
            t('workflowValidation.sendBackSingleIncoming', {
              name:
                targetNode?.data?.name || t('workflowBuilder.sendToAssessment'),
            })
          );
          return;
        }
        addNewEdge();
        return;
      }

      const isSourceActionNode =
        sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
        sourceNode?.category === NODE_CATEGORIES.CHILD_REJ ||
        sourceNode?.category === NODE_CATEGORIES.CHILD_SBK ||
        sourceNode?.category === NODE_CATEGORIES.CHILD_STA;

      if (isSourceActionNode) {
        const hasOutgoingEdge = currentEdges.some(
          (edge) => edge.source === params.source
        );
        if (hasOutgoingEdge) {
          toast.error(
            t('workflowValidation.actionNodeSingleConnection', {
              name: sourceNode?.data?.name || t('workflowBuilder.action'),
            })
          );
          return;
        }

        if (sourceNode?.category === NODE_CATEGORIES.CHILD_SBK) {
          const parentEdge = currentEdges.find(
            (edge) => edge.target === sourceNode.id
          );
          if (parentEdge) {
            const parentReviewNode = nodes.find(
              (n) => n.id === parentEdge.source
            );

            if (parentEdge.source === params.target) {
              toast.error(t('workflowValidation.sendBackLoopError'));
              return;
            }

            const upstreamOfParent = findUpstreamNodeIds(
              parentReviewNode.id,
              currentEdges,
              null
            );

            if (!upstreamOfParent.has(params.target)) {
              toast.error(t('workflowValidation.sendBackMustGoBackward'));
              return;
            }

            if (parentReviewNode?.category === NODE_CATEGORIES.REVIEW) {
              const reviewFormIds =
                parentReviewNode.data?.formConfig?.form_ids || [];
              const singleFormId = parentReviewNode.data?.formConfig?.form_id;
              const parentReviewForms = new Set(
                reviewFormIds.length > 0
                  ? reviewFormIds
                  : singleFormId
                    ? [singleFormId]
                    : []
              );

              const downstreamForms = findDownstreamFormIds(
                params.target,
                parentReviewNode.id,
                nodes,
                currentEdges
              );

              for (const formId of downstreamForms) {
                if (!parentReviewForms.has(formId)) {
                  toast.error(t('workflowValidation.sendBackInvalidTarget'));
                  return;
                }
              }

              if (targetNode?.category === NODE_CATEGORIES.REVIEW) {
                const parentRoleId =
                  parentReviewNode.data?.formConfig?.approver_role_id;
                const targetRoleId =
                  targetNode.data?.formConfig?.approver_role_id;

                if (
                  parentRoleId &&
                  targetRoleId &&
                  parentRoleId === targetRoleId
                ) {
                  toast.error(
                    t('workflowValidation.sendBackSameReviewerRole', {
                      name:
                        sourceNode?.data?.name || t('workflowBuilder.sendBack'),
                    })
                  );
                  return;
                }
              }
            }
          }
        }

        if (sourceNode?.category === NODE_CATEGORIES.CHILD_STA) {
          if (targetNode?.category !== NODE_CATEGORIES.ASSESSMENT_REVIEW) {
            toast.error(t('workflowValidation.sendToAssessmentInvalidTarget'));
            return;
          }
        }

        if (
          sourceNode?.category === NODE_CATEGORIES.CHILD_APP ||
          sourceNode?.category === NODE_CATEGORIES.CHILD_REJ
        ) {
          const edgesByTarget = new Map();
          for (const e of currentEdges) {
            if (!edgesByTarget.has(e.target)) {
              edgesByTarget.set(e.target, []);
            }
            edgesByTarget.get(e.target).push(e);
          }

          const incomingEdges = edgesByTarget.get(sourceNode.id) || [];

          for (const incomingEdge of incomingEdges) {
            const parentNode = nodes.find(
              (n) =>
                n.id === incomingEdge.source &&
                n.category === NODE_CATEGORIES.ASSESSMENT_REVIEW
            );

            if (parentNode) {
              const edgesToAssessmentReview =
                edgesByTarget.get(parentNode.id) || [];

              if (edgesToAssessmentReview.length === 0) {
                toast.error(
                  t('workflowValidation.AssessmentReviewNodeMustBeConnected')
                );
                return;
              }

              for (const edgeToAssessment of edgesToAssessmentReview) {
                const sendToAssessmentEdges =
                  edgesByTarget.get(edgeToAssessment.source) || [];

                if (sendToAssessmentEdges.length === 0) {
                  toast.error(
                    t('workflowValidation.SendToAssessmentNodeMustBeConnected')
                  );
                  return;
                }

                for (const sendToAssessmentEdge of sendToAssessmentEdges) {
                  if (sendToAssessmentEdge.source !== params.target) {
                    toast.error(
                      t(
                        'workflowValidation.approveRejectMustConnectToParentReview'
                      )
                    );
                    return;
                  }
                }
              }
            }
          }

          if (
            targetNode?.category === NODE_CATEGORIES.REVIEW ||
            targetNode?.category === NODE_CATEGORIES.SUBMISSION
          ) {
            const parentReviewEdge = incomingEdges[0];
            const parentReviewNode = parentReviewEdge
              ? nodes.find((n) => n.id === parentReviewEdge.source)
              : null;

            if (
              parentReviewNode?.category !==
                NODE_CATEGORIES.ASSESSMENT_REVIEW &&
              parentReviewNode
            ) {
              const primaryEdges = currentEdges.filter((e) => {
                const srcNode = nodes.find((n) => n.id === e.source);
                return srcNode?.category !== NODE_CATEGORIES.CHILD_SBK;
              });
              const upstreamOfParent = findUpstreamNodeIds(
                parentReviewNode.id,
                primaryEdges,
                null
              );
              if (upstreamOfParent.has(params.target)) {
                const key =
                  sourceNode?.category === NODE_CATEGORIES.CHILD_APP
                    ? 'workflowValidation.approveCannotGoBackward'
                    : 'workflowValidation.rejectCannotGoBackward';
                toast.error(
                  t(key, {
                    name: sourceNode?.data?.name || t('workflowBuilder.action'),
                  })
                );
                return;
              }
            }
          }
        }

        addNewEdge();
        return;
      }

      if (targetNode?.category === NODE_CATEGORIES.REVIEW) {
        const newEdge = {
          source: params.source,
          target: params.target,
        };
        const validation = validateReviewNodeForms(
          targetNode,
          currentEdges,
          newEdge
        );
        if (!validation.valid) {
          toast.error(validation.message);
          return;
        }
      }

      addNewEdge();
    },
    [setEdges, nodes, edges, validateReviewNodeForms, t]
  );

  return { onConnect };
};

export default useConnectionValidation;
